import React from "react";
import { BellIcon, CarIcon, CreditCardIcon, FileCheck2Icon, FileSearchIcon, FileTextIcon, ListChecksIcon, LogOutIcon, MailCheckIcon, UploadIcon, WalletIcon, BoxIcon } from "lucide-react";
import { CitizenScreen } from "../../types/navigation";
import { CitizenSession } from "./CitizenApp";
import { useStore } from "../../contexts/StoreContext";
import { MEDIA } from "../../data/media";
import { MobileHeader } from "../../components/mobile/MobileHeader";
import { CaseMiniCard } from "../../components/mobile/CaseMiniCard";
import { DOC_LABEL, isPaid } from "../../utils/caseStatus";
import { bn, taka } from "../../utils/bn";
import { buildNotifications } from "../../utils/notifications";
interface Props {
  session: CitizenSession;
  go: (s: CitizenScreen) => void;
  onLogout: () => void;
}
export function CitizenHome({
  session,
  go,
  onLogout
}: Props) {
  const {
    cases
  } = useStore();
  const mine = cases.filter(c => session.guestPcr ? c.pcr === session.guestPcr : c.mobile === session.mobile);
  const active = mine.filter(c => c.stage !== 'released');
  const primary = active.find(c => !c.letter) ?? active[0] ?? mine[0];
  const due = active.filter(c => !isPaid(c)).reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const approved = mine.filter(c => c.letter?.status === 'issued');
  const notes = buildNotifications(mine);
  const cards: {
    label: string;
    value: string;
    icon: BoxIcon;
    onClick: () => void;
    tone?: string;
  }[] = [{
    label: 'আমার যানবাহন',
    value: bn(mine.length),
    icon: CarIcon,
    onClick: () => go({
      name: 'search'
    })
  }, {
    label: 'চলমান PCR',
    value: bn(active.length),
    icon: FileTextIcon,
    onClick: () => go({
      name: 'search'
    })
  }, {
    label: 'কাগজপত্র জমা',
    value: primary ? DOC_LABEL[primary.docStatus].label : '—',
    icon: UploadIcon,
    onClick: () => primary && go({
      name: 'documents',
      pcr: primary.pcr
    }),
    tone: primary?.docStatus === 'correction' ? 'text-danger' : undefined
  }, {
    label: 'বকেয়া জরিমানা',
    value: taka(due),
    icon: WalletIcon,
    onClick: () => primary && go({
      name: 'payment',
      pcr: primary.pcr
    }),
    tone: due ? 'text-warning-700' : 'text-success'
  }, {
    label: 'অবমুক্তি অনুমোদন',
    value: bn(approved.length),
    icon: MailCheckIcon,
    onClick: () => approved[0] && go({
      name: 'letter',
      pcr: approved[0].pcr
    }),
    tone: approved.length ? 'text-success' : undefined
  }, {
    label: 'বিজ্ঞপ্তি',
    value: bn(notes.length),
    icon: BellIcon,
    onClick: () => go({
      name: 'notifications'
    })
  }];
  const actions: {
    label: string;
    icon: BoxIcon;
    onClick: () => void;
  }[] = [{
    label: 'PCR অনুসন্ধান',
    icon: FileSearchIcon,
    onClick: () => go({
      name: 'search'
    })
  }, {
    label: 'কাগজপত্র জমা দিন',
    icon: UploadIcon,
    onClick: () => primary && go({
      name: 'documents',
      pcr: primary.pcr
    })
  }, {
    label: 'জরিমানা পরিশোধ',
    icon: CreditCardIcon,
    onClick: () => primary && go({
      name: 'payment',
      pcr: primary.pcr
    })
  }, {
    label: 'আবেদনের অগ্রগতি',
    icon: ListChecksIcon,
    onClick: () => primary && go({
      name: 'progress',
      pcr: primary.pcr
    })
  }, {
    label: 'অবমুক্তি পত্র',
    icon: FileCheck2Icon,
    onClick: () => go({
      name: 'letter',
      pcr: approved[0]?.pcr ?? primary?.pcr ?? ''
    })
  }];
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন সেবা" right={<button onClick={onLogout} className="p-1.5" aria-label="লগআউট" data-id="element-899" data-mp="2jgb2v1h0qrtt"><LogOutIcon className="h-5 w-5" data-id="element-900" data-mp="24r56rmdh9hq5" /></button>} data-id="element-898" data-mp="2jfj79qnf8u3j" />
      <div className="flex-1 overflow-y-auto no-scrollbar" data-id="element-901" data-mp="24sg7tefjgbdk">
        <div className="relative mx-4 mt-4 overflow-hidden rounded-2xl" data-id="element-902" data-mp="29oydqhmlufrh">
          <img src={MEDIA.yard} alt="" className="h-32 w-full object-cover" data-id="element-903" data-mp="1vq70syjhb04x" />
          <div className="absolute inset-0 bg-navy-950/65 p-4 text-white" data-id="element-904" data-mp="1vsb176i65h0g">
            <p className="text-xs text-white/80" data-id="element-905" data-mp="20oi6qy6nkqhv">স্বাগতম,</p>
            <p className="text-lg font-bold" data-id="element-906" data-mp="1solpfei6a3yu">{session.name}</p>
            <p className="mt-1 text-xs text-white/85 leading-snug max-w-[240px]" data-id="element-907" data-mp="1sq6it0sr3gv4">অফিসে না গিয়েই কাগজপত্র জমা, জরিমানা পরিশোধ ও অগ্রগতি জানুন।</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 px-4 mt-3" data-id="element-908" data-mp="1iwzelj9qy6wm">
          {cards.map(c => <button key={c.label} onClick={c.onClick} className="card flex flex-col items-start p-2.5 text-left" data-id="element-909" data-mp="1evw4zeial73u">
              <c.icon className="h-4 w-4 text-navy" data-id="element-910" data-mp="19zfg2i39qopp" />
              <span className={`mt-1.5 text-sm font-bold leading-tight ${c.tone ?? 'text-ink'}`} data-id="element-911" data-mp="14z144oyjqume">{c.value}</span>
              <span className="text-[10px] leading-tight text-ink-muted" data-id="element-912" data-mp="zywn8xtq7xvn">{c.label}</span>
            </button>)}
        </div>
        <div className="px-4 mt-4" data-id="element-913" data-mp="vzed8x3g177l">
          <h2 className="mb-2 text-sm font-bold text-ink" data-id="element-914" data-mp="w0fpv603y1rf">প্রধান সেবাসমূহ</h2>
          <div className="card divide-y divide-line" data-id="element-915" data-mp="2ucwjigxzd1fh">
            {actions.map(a => <button key={a.label} onClick={a.onClick} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-navy-50" data-id="element-916" data-mp="2qd5mvwlrvr31">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-bdgreen-50 text-bdgreen" data-id="element-917" data-mp="c28p1hppmsco"><a.icon className="h-[18px] w-[18px]" data-id="element-918" data-mp="h1bkcafw1k7g" /></span>
                <span className="text-sm font-semibold text-ink" data-id="element-919" data-mp="lv7h59kf1111">{a.label}</span>
              </button>)}
          </div>
        </div>
        <div className="px-4 mt-4 mb-5" data-id="element-920" data-mp="puybfn95hzab">
          <h2 className="mb-2 text-sm font-bold text-ink" data-id="element-921" data-mp="3pbeivaj9so76">আমার আটককৃত যানবাহন</h2>
          <div className="space-y-2" data-id="element-922" data-mp="3u9y6rghfca26">
            {mine.map(c => <CaseMiniCard key={c.pcr} c={c} onClick={() => go({
            name: 'case',
            pcr: c.pcr
          })} data-id="element-923" data-mp="3uar7o0tpc5ce" />)}
            {mine.length === 0 && <p className="card p-5 text-center text-sm text-ink-muted" data-id="element-924" data-mp="75079i8h4qby">আপনার মোবাইল নম্বরে কোনো আটককৃত যানবাহন নেই।</p>}
          </div>
        </div>
      </div>
    </>;
}